/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 엘리먼트 기본클래스를 정의한다.
 *
 * @file /src/rootin/base/Element.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 3.
 */
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Block } from '@rootin/base/Block';
import { Editor } from '@rootin/core/Editor';

export interface Properties {
    /**
     * 엘리먼트명
     */
    name: string;
}

export abstract class Element {
    /**
     * 엘리먼트타입
     */
    abstract type: string;

    /**
     * 엘리먼트종류
     */
    abstract kind: 'text' | 'atomic' | 'container';

    /**
     * 엘리먼트명
     */
    name: string;

    /**
     * 엘리먼트 DOM
     */
    $element: Dom = null;

    /**
     * 편집전 캐럿 상태 (캐럿이 존재하지 않는 엘리먼트인 경우 항상 NULL)
     */
    #beforeCaret: any = null;

    /**
     * 엘리먼트 기본클래스를 정의한다.
     *
     * @param block - 이 엘리먼트가 속한 블록
     * @param properties - 설정
     */
    constructor(
        public block: Block,
        public properties: Properties
    ) {
        this.name = properties.name;
    }

    /**
     * 이 Element 가 속한 에디터를 가져온다. `block.editor`를 그대로 넘겨주는 편의 메서드 —
     * 모든 Element 가 `this.getEditor()`로 바로 접근 가능하게 한다.
     *
     * @returns 에디터
     */
    getEditor(): Editor {
        return this.block.editor;
    }

    /**
     * 엘리먼트고유값을 가져온다.
     *
     * @returns 엘리먼트고유값
     */
    getId(): string {
        return `${this.block.getId()}-${this.name}`;
    }

    /**
     * 엘리먼트명을 가져온다.
     *
     * @returns 이름
     */
    getName(): string {
        return this.name;
    }

    /**
     * 엘리먼트 DOM을 가져온다
     *
     * @returns 엘리먼트 DOM
     */
    $getElement(): Dom {
        if (this.$element === null) {
            const el = document.createElement('rootin-element') as RootinElement;
            el.setElement(this);

            this.$element = Html.el(el) as Dom;
            this.$element.setAttr('data-element', this.getId());
            this.$element.setAttr('data-type', this.type);
            this.$element.setAttr('data-name', this.name);

            if (this.kind == 'text' || this.kind == 'container') {
                this.$element.setAttr('contenteditable', 'true');
                this.$element.setAttr('data-editable', 'true');
            } else {
                this.$element.setAttr('contenteditable', 'false');
                this.$element.setAttr('data-editable', 'false');
            }

            this.$element.setData('element', this, false);
        }

        return this.$element;
    }

    /**
     * 현재 캐럿 오프셋을 가져온다.
     *
     * @returns 캐럿 오프셋
     */
    captureCaret(): any {
        return null;
    }

    /**
     * 캐럿 오프셋을 복원한다.
     *
     * @param state - 복원할 캐럿 오프셋
     */
    restoreCaret(state: any): void {}

    /**
     * 엘리먼트에 포커스를 지정한다.
     *
     * @param to_start - 포커스 위치 (TRUE : 처음, FALSE : 끝, NULL : 앵커)
     */
    focus(to_start: boolean = false): void {
        this.getEditor().caret.collapse(this, to_start);
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    abstract render(): Promise<void>;

    /**
     * 엘리먼트가 비었는지 확인한다.
     *
     * @returns 엘리먼트가 비었는지 여부
     */
    abstract isEmpty(): boolean;

    /**
     * 엘리먼트값을 가져온다.
     *
     * @returns 엘리먼트값
     */
    abstract getValue(): any;

    /**
     * 엘리먼트값을 지정한다.
     *
     * @param value - 엘리먼트값
     */
    abstract setValue(value: any): Promise<void>;

    /**
     * 엘리먼트가 포커스를 받았을 때 이벤트를 처리한다.
     */
    onFocus(): void {}

    /**
     * 엘리먼트가 포커스를 잃었을 때 이벤트를 처리한다.
     */
    onBlur(): void {}

    /**
     * 엘리먼트내에서 Enter 키입력이 발생했을 때 이벤트를 처리한다.
     */
    onEnter(): void {
        this.block.onEnter(this);
    }

    /**
     * 편집이 일어나기 직전 이벤트를 처리한다.
     */
    onBeforeEdit(): void {
        this.#beforeCaret ??= this.captureCaret();
    }

    /**
     * 편집이 일어난 이후 이벤트를 처리한다.
     */
    onEdit(): void {
        this.getEditor().history.recordEdit(this, this.#beforeCaret);
        this.#beforeCaret = null;
        this.block.onEdit(this);
        this.getEditor().onChange();
    }

    /**
     * 캐럿을 엘리먼트의 처음 또는 끝으로 이동한다.
     *
     * @param to_start - true 면 맨 앞으로, false 면 맨 끝으로
     * @returns 직접 처리했으면 true (Caret 의 범용 폴백을 막음)
     */
    onCaretEdge(to_start: boolean): boolean {
        return false;
    }

    /**
     * 이 엘리먼트가 DOM 에 연결되었을 때 이벤트를 처리한다.
     */
    async onConnect(): Promise<void> {}

    /**
     * 이 엘리먼트가 DOM 에서 연결해제되었을 때 이벤트를 처리한다.
     */
    async onDisconnect(): Promise<void> {}
}

/**
 * 엘리먼트 루트 DOM
 */
export class RootinElement extends HTMLElement {
    element: Element = null;

    setElement(element: Element): void {
        this.element = element;
    }

    connectedCallback(): void {
        this.element?.onConnect();
    }

    disconnectedCallback(): void {
        this.element?.onDisconnect();
    }
}
customElements.define('rootin-element', RootinElement);
